import React, { useCallback, useEffect, useState } from "react";
import { IBreed, IEvent, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { helperService } from "src/services/helper.service";
import { AxiosError } from "axios";
import { toast } from "../../../../components/Toast/ToastManager"
import { apiService } from "../../../../services/api.service";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";

import SearchDown from "../../../SearchDown/SearchDown";
import star from "../../../../assets/icons/star.svg";

import { ShowTypes, CWAGS_GAMES_ZOOM_LEVELS, getLevelSections, isCwagsFeoShowWide } from "@eagerdog/constants";

import {
  getElementSelectionState,
  INestedShowElement,
  shouldUseNestedSetup,
  showSectionAssociation
} from "../utils";
import { ShowElementsDepthBuilder } from "../utils/ShowElementsDepthBuilder";

import styles from "./ShowTypeTab.module.scss";

interface IProps {
  event: IEvent,
  show?: IShow,
  onChange(clubName: string, showType: IOption, showElements: ICheck[], elementJudges: ICheck[], armNum: number, breedSearch: string, splashCount?: number, showElementsNested?: INestedShowElement[], feoAvailable?: boolean): void
}

// AKC Conformation class restrictions
const AKC_REGULAR_CLASSES = [
  Constants.dog_class_element_level.akc_6_9_month_puppy,
  Constants.dog_class_element_level.akc_9_12_month_puppy,
  Constants.dog_class_element_level.akc_12_18_month_puppy,
  Constants.dog_class_element_level.akc_18_24_month_puppy,
  Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_amateur_owner_handler,
  Constants.dog_class_element_level.akc_american_bred,
  Constants.dog_class_element_level.akc_open,
  Constants.dog_class_element_level.akc_exhibition_only
];

// No longer limiting regular classes - handled by multiple judge books if > 8
// const MAX_REGULAR_CLASSES = 8; // Removed - no limit on regular classes
const MAX_VETERAN_CLASSES = 6;

// Utility functions for class categorization
const isRegularClass = (classLevel: string): boolean => {
  return AKC_REGULAR_CLASSES.includes(classLevel);
};

const isVeteranClass = (classLevel: string): boolean => {
  const lowerClass = classLevel.toLowerCase();
  // Exclude veteran sweepstakes classes - they are their own group
  if (lowerClass.includes('veteran') && lowerClass.includes('sweepstakes')) {
    return false;
  }
  return lowerClass.includes('veteran');
};

const isRestrictedClass = (classLevel: string): boolean => {
  return isRegularClass(classLevel) || isVeteranClass(classLevel);
};

// ABI SDG Foundations levels default to Level 1 only when creating a new show
const SDG_FOUNDATIONS_LEVELS = new Set([
  Constants.dog_class_element_level.sdg_twice_around_foundations,
  Constants.dog_class_element_level.sdg_clear_it_foundations,
  Constants.dog_class_element_level.sdg_cluster_foundations,
  Constants.dog_class_element_level.sdg_hi_lo_foundations,
  Constants.dog_class_element_level.sdg_hit_the_wall_foundations,
]);

// C-WAGS Games Zoom variants are rarely offered — default them unchecked on a new show
const CWAGS_GAMES_ZOOM_LEVEL_SET = new Set(CWAGS_GAMES_ZOOM_LEVELS);

const ShowTypeTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [clubName, setClubName] = useState<string>(props.show?.club_name ?? props.event.club_name ?? props.event.club?.club_name ?? "");
  const [showType, setShowType] = useState<IOption>({ id: "None", value: "None" });

  const getArmBandNumStart = useCallback((_showType: string) => {
    let hasArmNum = props.event.arm_numbers_config?.filter((a: any) =>  a.show_type === _showType);

    if (hasArmNum && hasArmNum.length > 0) {
      return hasArmNum[0].arm_number_start.toString();
    }

    return "100";
  }, [props.event.arm_numbers_config]);

  const [showTypes, setShowTypes] = useState<IOption[]>([]);
  const [armNum, setArmNum] = useState<string>(getArmBandNumStart(showType.id));
  const [showElements, setShowElements] = useState<ICheck[]>([]);
  const [showElementJudges, setShowElementJudges] = useState<ICheck[]>([]);

  const [showElementsNested, setShowElementsNested] = useState<INestedShowElement[]>([]);

  const [breedList, setBreedList] = useState<IBreed[]>([]);
  const [breedSearch, setBreedSearch] = useState<string>("");
  const [splashCount, setSplashCount] = useState<number>(6); // Default to 6 splashes
  // C-WAGS Scent / Games: show-wide "FEO Available" toggle. New shows default ON; existing
  // shows keep their saved value (undefined -> off) so live events don't gain FEO with no price.
  const [feoAvailable, setFeoAvailable] = useState<boolean>(props.show ? !!props.show.feo_available : true);
  const isUkcBreedGroupOrder =
    props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
    props.event.armband_order_type === Constants.armband_order_type.breed_group_order;

  const showTypeHasElements = (showType: string) => {
    if (ShowTypes[props.event.sanctioning_club][showType][showType] !== undefined) {
      if (ShowTypes[props.event.sanctioning_club][showType][showType] !== undefined && 
          ShowTypes[props.event.sanctioning_club][showType][showType].length === 1 && 
          ShowTypes[props.event.sanctioning_club][showType][showType][0] === showType) {
        return false;
      }
    }

    return true;
  }
  
  const getShowTypeOptions = useCallback((sanctioningClub: string) => {
    let options:IOption[] = [];

    // NOTE: AKC only supports Scentwork for lottery events at the moment
    if (props.event.is_lottery && sanctioningClub === Constants.sanctioning_club.AKC) {
      options.push({
        value: Constants.show_type.scentwork,
        id: Constants.show_type.scentwork,
      });

      return options;
    }

    for (let i in ShowTypes[sanctioningClub]) {
      options.push({
        value: i,
        id: i
      });
    }

    options.sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: "base" }));

    return options;
  }, [props.event.is_lottery]);

  const pushCheckListOption = (options: ICheck[], label: string, checked: boolean) => {
    options.push({ label, checked });
  }

  const buildNestedShowElements = useCallback((): INestedShowElement[] => {
    const showSetup = ShowTypes[props?.event?.sanctioning_club][showType.id];
    // Only add sections for shows that actually use them during setup
    // UKC Nosework does NOT get sections here - sections are added during registration
    const sections = showSectionAssociation[`${props?.event?.sanctioning_club}_${showType.id}`];

    if (!!showSetup) {
      const result = Object.entries(showSetup).map(([element, value]) => {
        // value can be a plain string[] (old format) or IShowTypeElementConfig {levels, sections}
        const levels: string[] = Array.isArray(value) ? value : ((value as any)?.levels ?? []);
        const elementConfigSections: string[] | undefined = Array.isArray(value) ? undefined : ((value as any)?.sections ?? undefined);
        // Use sections from showSectionAssociation (show-level) or elementConfig (element-level)
        const effectiveSections = sections ?? elementConfigSections;
        
        // For ABI Urban Rat Race, only Tubes, Unobstructed, Obstructed, and Obstructed 2 have sections (not Games)
        const shouldHaveSections = effectiveSections && !(
          props?.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
          showType.id === Constants.show_type.urban_rat_race &&
          element === Constants.dog_class_element.games
        );

        // For element-level sections (no levels), attach sections directly to element
        const elementSections = (shouldHaveSections && levels.length === 0) ? effectiveSections!.map((section) => ({
          section,
          sectionSelected: props.show?.show_elements ?
            getElementSelectionState(props.show.show_elements, element, undefined, section) : true,
        })) : undefined;

        return {
          element,
          elementSelected: props.show?.show_elements ?
            getElementSelectionState(props.show?.show_elements, element) : true,
          showLevels: levels.map((level) => {
            const isCwagsGamesZoom =
              props?.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
              showType.id === Constants.show_type.cwags_games &&
              CWAGS_GAMES_ZOOM_LEVEL_SET.has(level);
            // Some levels offer a subset of the element's sections (UKC Rally: RO1 A/B/C,
            // RO2-ROM A/B, URX/RMX none). A level with no sections is a plain checkbox.
            const levelSections = shouldHaveSections
              ? getLevelSections(props?.event?.sanctioning_club, showType.id, element, level, effectiveSections)
              : [];
            return {
              level,
              levelSelected: props.show?.show_elements ?
                getElementSelectionState(props.show?.show_elements, element, level) : !isCwagsGamesZoom,
              sections: (shouldHaveSections && levels.length > 0 && levelSections.length > 0) ? levelSections.map((section) => {
                return {
                  section,
                  sectionSelected: props.show?.show_elements ?
                    getElementSelectionState(props.show?.show_elements, element, level, section) :
                    !(
                      SDG_FOUNDATIONS_LEVELS.has(level) &&
                      section === Constants.dog_class_section.sdg_level_2
                    ),
                }
              }) : undefined,
            }
          }),
          elementSections,
        }
      });
      
      return result;
    }

    return [];
  }, [props?.event?.sanctioning_club, props.show?.show_elements, showType.id]);

  const toggleCheckNestedElement = (
    element: string,
    level?: string,
    section?: string
  ) => {
    // clone
    const newState = JSON.parse(JSON.stringify(showElementsNested)) as INestedShowElement[];

    const _element = newState.find((n) => n.element === element);
    if (_element && !level && !section) _element.elementSelected = !_element?.elementSelected;

    // unselected inner items (level + sections)
    if (_element && !_element.elementSelected) {
      for (const el of _element.showLevels) {
        el.levelSelected = false;

        for (const ls of el.sections ?? []) {
          ls.sectionSelected = false;
        }
      }
      // also deselect element-level sections
      for (const es of _element.elementSections ?? []) {
        es.sectionSelected = false;
      }
    }

    // Handle element-level section toggle (no intermediate level, e.g. ABI Scent Dog Games)
    if (section && !level) {
      const _section = _element?.elementSections?.find((s) => s.section === section);
      if (_section && _element?.elementSelected) _section.sectionSelected = !_section.sectionSelected;
    }

    if (level) {
      const _level = _element?.showLevels.find((n) => n.level === level);

      // make sure related Element is selected
      if (_level && !section && !!_element?.elementSelected) _level.levelSelected = !_level?.levelSelected;

      // unselected inner items (sections)
      if (_level && !_level.levelSelected) {
        for (const ls of _level.sections ?? []) {
          ls.sectionSelected = false;
        }
      }

      if (section) {
        const _section = _level?.sections?.find((s) => s.section === section);

        // make sure related Level is selected
        if (_section && !!_level?.levelSelected) _section.sectionSelected = !_section?.sectionSelected;
      }

    }

    setShowElementsNested(newState);
  };

  // Keys in ShowTypes whose array lists individually checkable classes rather than levels
  // of one element (UKC non-licensed classes, CKC obedience and rally unofficial classes).
  const groupedShowElements: string[] = [
    Constants.dog_class_element.conformation_non_licensed,
    Constants.dog_class_element.obedience_unofficial,
    Constants.dog_class_element.rally_unofficial,
  ];

  const getShowElementOptions = useCallback((newShowType: string) => {
    let options:ICheck[] = [];

    // show edit
    if (props.show && props.show.show_type === newShowType) {
      
      for (let showElement in ShowTypes[props.event.sanctioning_club][newShowType]) {
        
        // for licensed elements
        if (!groupedShowElements.includes(showElement)) {
          let hasShowElement = props.show.show_elements.filter((se) => {
            const levelMatch = se.level === showElement;
            const showElementMatch = se.show_element === showElement;
            return se.level ? levelMatch : showElementMatch;
          });

          pushCheckListOption(options, showElement, !!hasShowElement.length);
        // for non-licensed elements
        } else {
          for (let showLevel in ShowTypes[props.event.sanctioning_club][newShowType][showElement]) {
            const element = ShowTypes[props.event.sanctioning_club][newShowType][showElement];
            let hasShowLevel = props.show.show_elements.filter((se) => {
              return se.level ? se.level === element[showLevel] : se.show_element === element[showLevel];
            });

            pushCheckListOption(options, element[showLevel], !!hasShowLevel.length);
          }
        }
      }
    // show new
    } else {
      // Default selections for AKC conformation shows
      const isAkcConformation = props.event.sanctioning_club === Constants.sanctioning_club.AKC && 
                               newShowType === Constants.show_type.conformation;
      
      const defaultAkcConformationClasses = [
        Constants.dog_class_element_level.akc_6_9_month_puppy,
        Constants.dog_class_element_level.akc_9_12_month_puppy,
        Constants.dog_class_element_level.akc_12_18_month_puppy,
        Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
        Constants.dog_class_element_level.akc_american_bred,
        Constants.dog_class_element_level.akc_open,
        Constants.dog_class_element_level.akc_best_of_breed
      ];

      for (let showElement in ShowTypes[props.event.sanctioning_club][newShowType]) {
        if (!groupedShowElements.includes(showElement)) {
          // For AKC conformation, only check the default classes
          const shouldCheck = isAkcConformation ? 
            defaultAkcConformationClasses.includes(showElement) : 
            true;
          pushCheckListOption(options, showElement, shouldCheck);
        } else {
          for (let showLevel in ShowTypes[props.event.sanctioning_club][newShowType][showElement]) {
            const elementValue = ShowTypes[props.event.sanctioning_club][newShowType][showElement][showLevel];
            // For AKC conformation, only check the default classes
            const shouldCheck = isAkcConformation ? 
              defaultAkcConformationClasses.includes(elementValue) : 
              true;
            pushCheckListOption(options, elementValue, shouldCheck);
          }
        }
      }
    }

    return options;
  }, [props.event.sanctioning_club, props.show]);

  // NOTE: AKC Fetch only feature; the show has no non-licensed elements
  const getElementJudgeOptions = useCallback((showType: string): ICheck[] => {
    const options: ICheck[] = [];

    // show edit
    if (props.show) {
      return props.show.show_elements.map((se) => {
        return {
          label: se.level ?? se.show_element,
          checked: !!se.use_judge_name,
        }
      }) as ICheck[];
    // show new
    } else {
      for (let showElement in ShowTypes[props.event.sanctioning_club][showType]) {
        pushCheckListOption(options, showElement, false);
      }
    }

    return options;
  }, [props.event.sanctioning_club, props.show]);

  // Helper functions for counting selected classes
  const getClassCounts = (elements: ICheck[]) => {
    let regularCount = 0;
    let veteranCount = 0;
    
    elements.forEach(element => {
      if (element.checked) {
        if (isRegularClass(element.label)) {
          regularCount++;
        } else if (isVeteranClass(element.label)) {
          veteranCount++;
        }
      }
    });
    
    return { regularCount, veteranCount };
  };

  const canToggleClass = (classLevel: string, currentlyChecked: boolean, elements: ICheck[]): boolean => {
    // If we're unchecking, always allow
    if (currentlyChecked) {
      return true;
    }
    
    // If it's not a restricted class, always allow
    if (!isRestrictedClass(classLevel)) {
      return true;
    }
    
    // Check limits for restricted classes
    const { veteranCount } = getClassCounts(elements);
    
    if (isRegularClass(classLevel)) {
      return true; // No limit on regular classes
    }
    
    if (isVeteranClass(classLevel)) {
      return veteranCount < MAX_VETERAN_CLASSES;
    }
    
    return true;
  };

  const toggleCheck = (index: number) => {
    let _options:ICheck[] = [...showElements];
    const element = _options[index];
    
    // Only apply restrictions for AKC conformation shows
    if (props.event.sanctioning_club === Constants.sanctioning_club.AKC && 
        showType.value === Constants.show_type.conformation) {
      
      if (!canToggleClass(element.label, element.checked, _options)) {
        // Show a toast message explaining the restriction
        const { veteranCount } = getClassCounts(_options);
        
        // Regular classes have no limit, so this should not trigger
        // Only veteran classes are restricted
        if (isVeteranClass(element.label)) {
          toast.show({
            title: "Class Selection Limit", 
            content: `You can only select up to ${MAX_VETERAN_CLASSES} veteran classes. You currently have ${veteranCount} selected.`,
            duration: 5000,
            type: "warning"
          });
        }
        return; // Don't toggle the checkbox
      }
    }

    _options[index].checked = !_options[index].checked;
    setShowElements(_options);
  }

  const updateSplashCount = (count: number) => {
    setSplashCount(count);
    // Trigger onChange to update parent component
    onChange(clubName, showType, showElements, showElementJudges, parseInt(armNum), breedSearch, count, showElementsNested, feoAvailable);
  }

  const toggleElementJudgeCheck = (index: number) => {
    const _options: ICheck[] = [...showElementJudges];

    _options[index].checked = !_options[index].checked;

    setShowElementJudges(_options);
  }

  const pShowType:any = helperService.usePrevious(showType);

  useEffect(() => {
    if (showType !== pShowType) {
      let _options:ICheck[] = getShowElementOptions(showType.id);
      setShowElements(_options);
      setArmNum(getArmBandNumStart(showType.id));

      if (shouldUseNestedSetup(showType.id, props.event.sanctioning_club)) {
        setShowElementsNested(buildNestedShowElements());
      }

      if (Constants.show_type.fetch === showType.value) {
        let judge_options:ICheck[] = getElementJudgeOptions(showType.id);
        setShowElementJudges(judge_options);
      }
    }
  }, [showType, pShowType, getShowElementOptions, getElementJudgeOptions, getArmBandNumStart, buildNestedShowElements, props.event?.sanctioning_club]);

  let { onChange } = props;

  useEffect(() => {
    // Always call onChange when showElements, armNum, breedSearch, or splashCount change
    if (loaded && showElements.length > 0) {
      onChange(clubName, showType, showElements, showElementJudges, parseInt(armNum), breedSearch, splashCount, showElementsNested, feoAvailable);
    }
  }, [onChange, clubName, showType, showElements, showElementJudges, showElementsNested, armNum, breedSearch, splashCount, loaded, feoAvailable]);

  useEffect(() => {
    if (!loaded) {
      const hydrateShowTypeTab = () => {
        if (props.show) {
          
          setShowType({ value: props.show.show_type, id: props.show.show_type });
          setArmNum(props.show.arm_number_start?.toString() ?? getArmBandNumStart(props.show.show_type));
          setBreedSearch(props.show.specialty_breed ?? "");
          
          // Get splash count from the Air element if it exists
          const airElement = props.show.show_elements.find(element => 
            element.show_element === Constants.dog_class_element.dock_jumping_air
          );
          const initialSplashCount = airElement?.splash_count ?? 6;
          setSplashCount(initialSplashCount);
          
          // CRITICAL: Initialize showElements state with checked states from existing show
          let _elementOptions: ICheck[] = getShowElementOptions(props.show.show_type);
          setShowElements(_elementOptions);
          
          // Initialize showElementJudges
          let _elementJudgeOptions: ICheck[] = getElementJudgeOptions(props.show.show_type);
          setShowElementJudges(_elementJudgeOptions);
        }
      }

      if (props.event) {
        let _options:IOption[] = getShowTypeOptions(props.event.sanctioning_club);

        setShowTypes(_options);

        if (!props.show) {
          setShowType(_options[0]);
        } else {
          hydrateShowTypeTab();
        }
      }

      setLoaded(true);
    }
  }, [loaded, props.event, props.show, getArmBandNumStart, getShowTypeOptions, getShowElementOptions, getElementJudgeOptions]);

  const handleBreedSearch = async () => {
    try {
      const breeds = await apiService.getBreeds();
      setBreedList(breeds);
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        toast.show({
          title: "Dog Breeds",
          content: "Unable to get dog breeds",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  };

  return (
    <div className={styles.ShowTypeTab}>
      <div className="stepTitle">Show Type and Elements</div>
      <p>What type of show are you running?</p>
      <Input required label="Club Name" defaultValue={clubName} type="text" onChange={(e) => { e.stopPropagation(); setClubName(e.target.value); }} />
      {(!!props.event.is_lottery && props.event.sanctioning_club === Constants.sanctioning_club.AKC) && <div className={styles.lotteryWarning}>
        AKC only supports Scentwork for random draw style events at the moment
      </div>}
      <Dropdown value={showType} onChange={(e: any, value: IOption) => {
        setShowType(value);
      }} label="Show Type" options={showTypes} placeholder="Choose a Show Type" />
      {props.event.sanctioning_club === Constants.sanctioning_club.AKC && (showType.value === Constants.show_type.conformation || showType.value === Constants.show_type.obedience || showType.value === Constants.show_type.rally_obedience) &&
          <SearchDown
          label="Specialty Breed"
          icon={star}
          id="specialtyBreed"
          type="text"
          onChange={(e) => {
            e.stopPropagation();
            setBreedSearch(e.target.value);
          }}
          onSelect={(value: string) => {
            setBreedSearch(value);
          }}
          onClear={() => {
            setBreedSearch("");
          }}
          placeholder="If this is a specialty show, please select the breed."
          onSearch={handleBreedSearch}
          searchResults={breedList.filter((b: IBreed) => {
            if (breedSearch && breedSearch.length > 0 && b.breed.toLowerCase().includes(breedSearch.toLowerCase())) {
              return b.breed;
            } else {
              return false;
            }}).map((b: IBreed) => b.breed).sort()
          }
          defaultValue={breedSearch}
          showClear={true}
        />
      }
      {((props.event.sanctioning_club === Constants.sanctioning_club.AKC) ||
        (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
          props.event.armband_generation === Constants.armband_generation.for_show &&
          !isUkcBreedGroupOrder)) && (
        <Input
          className={styles.armBandStart}
          required
          onChange={(e) => { setArmNum(e.target.value); }}
          type="number"
          label="Armband Starting Number"
          placeholder="Enter an armband starting number."
          defaultValue={armNum}
        />
      )}
      {((props.event.sanctioning_club === Constants.sanctioning_club.AKC) ||
        (props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
          props.event.armband_generation === Constants.armband_generation.for_show &&
          !isUkcBreedGroupOrder)) && (
        <p className={styles.hint}>
          Armband starting numbers are based on show type. Changing the armband starting number will change the starting number for all shows of this show type.
        </p>
      )}

      {/* C-WAGS Scent / Games: FEO (For Exhibition Only) is a single show-wide toggle.
          Obedience / Rally offer FEO per level as a section checkbox in the tree below instead. */}
      {isCwagsFeoShowWide(props.event.sanctioning_club, showType.id) &&
        <div className="judgeCheckWrap">
          <Checkbox
            id="feoAvailable"
            label="FEO Available (For Exhibition Only runs can be entered for every class offered below)"
            value={feoAvailable}
            onChange={() => { setFeoAvailable(!feoAvailable); }}
          />
        </div>
      }

      {/* Nested elements (for C-WAGS shows with sections, Herding, Scent, ISWS Conformation, etc.) */}
      {shouldUseNestedSetup(showType.id, props.event.sanctioning_club) &&
        <div className={styles.nestedElementsWrap}>
          <ShowElementsDepthBuilder
            showElementsNested={showElementsNested}
            onChange={toggleCheckNestedElement}
            defaultCollapsed={props.event.sanctioning_club === "AHBA"}
          />
        </div>
      }

      {/* Regular checkboxes (for AKC Rally/Obedience, AKC Conformation, and other non-nested shows) */}
      <>{!shouldUseNestedSetup(showType.id, props.event.sanctioning_club) &&
         showElements.length > 0 && showTypeHasElements(showType.id) &&
        <div>
          <p>What elements are available at your show?</p>

          {/* Show class selection counters for AKC conformation */}
          {props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
            showType.value === Constants.show_type.conformation &&
            (() => {
              const { veteranCount } = getClassCounts(showElements);
              const hasRegularClasses = showElements.some(e => isRegularClass(e.label));
              const hasVeteranClasses = showElements.some(e => isVeteranClass(e.label));

              return (hasRegularClasses || hasVeteranClasses) && (
                <div className={styles.classCounters}>
                  {hasVeteranClasses && (
                 <div className={`${styles.counter} ${veteranCount >= MAX_VETERAN_CLASSES ? styles.atLimit : ''}`}>
                   <strong>Veteran Classes:</strong><span className={styles.counterText}>{veteranCount} / {MAX_VETERAN_CLASSES} selected</span>
                   {veteranCount >= MAX_VETERAN_CLASSES && <span className={styles.limitWarning}>(Limit reached)</span>}
                 </div>
               )}
             </div>
           );
         })()}
        
        {showElements.map((check:ICheck, index:number) => {
          const isAirElement = check.label === Constants.dog_class_element.dock_jumping_air;
          
          // Determine if checkbox should be disabled based on AKC conformation restrictions
          let isDisabled = false;
          if (props.event.sanctioning_club === Constants.sanctioning_club.AKC && 
              showType.value === Constants.show_type.conformation) {
            isDisabled = !canToggleClass(check.label, check.checked, showElements);
          }
          
          return(
            <div key={"showType" + index}>
              <div className={styles.showTypeWrap}>
                <Checkbox
                  key={"elem" + index}
                  id={check.label}
                  label={check.label}
                  value={check.checked}
                  disabled={isDisabled}
                  onChange={(e) => {
                    toggleCheck(index);
                  }}
                />
                {Constants.show_type.fetch === showType.value && <Checkbox
                  id={check.label + "judge-name"}
                  label="Use judge name in scoresheets"
                  value={showElementJudges.find((elementJudge) => elementJudge.label === check.label)?.checked ?? false}
                  onChange={(e) => {
                    toggleElementJudgeCheck(index);
                  }}
                />}
              </div>
              {/* Splash count input positioned right after Air checkbox */}
                             {props.event.sanctioning_club === Constants.sanctioning_club.UKC && 
                showType.value === Constants.show_type.dock_jumping && 
                isAirElement && 
                                 <div className={styles.splashInputContainer}>
                   <Dropdown
                     label="Number of Splashes"
                     value={{ value: splashCount.toString(), id: splashCount.toString() }}
                     onChange={(e: any, value: IOption) => {
                       const count = parseInt(value.value);
                       updateSplashCount(count);
                     }}
                     options={[
                       { value: "1", id: "1" },
                       { value: "2", id: "2" },
                       { value: "3", id: "3" },
                       { value: "4", id: "4" },
                       { value: "5", id: "5" },
                       { value: "6", id: "6" }
                     ]}
                     placeholder="Select number of splashes"
                   />
                 </div>}
            </div>
          );
        })}
      </div>}</>
    </div>
  );
};

export default ShowTypeTab;